/**
 * Created by Yoyo Fang on 09/11/19.
 * Description:
 *  implement interface of dashboard tiles for tam dashboard thread tile
 * ------ maintenance history ------
 * 2019-02-28 Ella - Add print(setTileData)
 */
import { DashboardTileAction, TamDashboardThreadBaseConfiguration } from '../tam-dashboard-tile.base.config';
import { Subject, Subscription } from 'rxjs';
import { TamDashboardActions, TamDashboardEvents, TileIcons } from '../tam-dashboard.model';
import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import { ThreadDetailConfig } from '../../thread-detail/thread-detail.view-model';
import { Store } from '@ngrx/store';
import { DELETE_ATTACHMENT_SUCCESS, UPDATE_NOTE_SUCCESS, DELETE_SIDENOTE_SUCCESS, DELETE_NOTE, DELETE_NOTE_SUCCESS } from '../../../tamalelibs/redux/note.actions';
import { NoteTileSettingsService } from '../../dashboard/tile-setting/tile-chart-setting/note-tile-chart-setting/note-tile-chart-setting.service';
import { DashboardActionTypes } from '../../../redux/actions/dashboard.actions';
import { DashboardTileService } from '../dashboard-tile.service';
import { DEFAULT_SETTINGS_DATA } from '../../dashboard/tile-setting/tile-chart-setting/note-tile-chart-setting/note-tile-chart-setting.model';
import { lastCustomActionSelector } from '../../../redux/reducers/action-history.reducer';

@Injectable()
export class TamDashboardThreadTileConfiguration implements TamDashboardThreadBaseConfiguration {
    public id: string; // representing tile instance
    public initialized: boolean;
    /**
     * a flag indicating whether this component is working under printing mode.
     */
    public isPrintingMode: boolean;
    public name: string;
    public readonly: boolean;
    public threadDetailConfigs: Map<string, ThreadDetailConfig>;
    public clickable: boolean;

    public actionSubject$: Subject<any>;
    public feedbackSubject$: Subject<any>;

    public subscriptions: Subscription[] = [];

    private _widget: any;
    private _tileConfig: any = null;
    private _noteIds = [];
    private _attachmentIds = [];
    constructor(
        private _store?: Store<any>,
        private _dashboardTileService?: DashboardTileService,
    ) {
        this.initialized = false;
        this.threadDetailConfigs = new Map<string, ThreadDetailConfig>();
    }

    destroy() {
        this.subscriptions.forEach((subscription: Subscription) => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
    }

    init(id: string, readonly: boolean, name: string, actionSubject$: Subject<any>, feedbackSubject$: Subject<any>): boolean {
        if (this.initialized === false) {
            this.id = id;
            this.readonly = readonly;
            this.name = name;
            this.actionSubject$ = actionSubject$;
            this.feedbackSubject$ = feedbackSubject$;
            const actionSubscription = this.actionSubject$.pipe(
                filter((action: any) => action && action.id === this.id)
            ).subscribe(this._actionHandler.bind(this));
            this.subscriptions.push(actionSubscription);
            const feedbackSubscription = this.feedbackSubject$.pipe(
                filter((action: any) => action && action.payload && action.payload.id === this.id)
            ).subscribe(this._feedbackHandler.bind(this));
            this.subscriptions.push(feedbackSubscription);
            if (this._store) {
                this.subscriptions.push(this._store.select<any>(lastCustomActionSelector)
                    .pipe(
                        filter(this._storeFilter.bind(this))
                    )
                    .subscribe(this._storeHandler.bind(this)));
            }
            this.initialized = true;
            return true;
        } else {
            return false;
        }
    }

    setWidget(widget) {
        this._widget = widget;
        const isNewTile = widget.tile && !widget.tile.id;
        widget.tile = NoteTileSettingsService.prepareDefaultConfig(widget.tile, DEFAULT_SETTINGS_DATA, isNewTile);
    }

    setTileData(data) {
        if (data) {
            this.actionSubject$.next({
                id: this.id,
                type: TamDashboardActions.SET_TILE_DATA,
                payload: data.displaySettings,
                threadConfigs: data.threadConfigs,
                hardRefresh: data.hardRefresh
            });
        } else {
            this.actionSubject$.next({
                id: this.id,
                type: TamDashboardActions.SET_TILE_DATA,
                payload: data
            });
        }
    }

    private _actionHandler(action: DashboardTileAction) {
        switch (action.type) {
            case TamDashboardActions.LOAD_DATA: {
                if (action.payload) {
                    this._tileConfig = action.payload;
                    this._reload(action.payload);
                }
                break;
            }
        }
    }

    private _feedbackHandler(feedback: DashboardTileAction) {
        switch (feedback.type) {
            case TamDashboardEvents.ITEM_ICON_CLICK: {
                if (feedback.payload.icon === TileIcons.REFRESH.icon && this.threadDetailConfigs.size) {
                    this._reload(this._tileConfig, true);
                }
            }
        }
    }

    private _storeHandler(event) {
        this._setLoadingStatus(true);
        // delete note success has no note id info, so we use delete note for store change detection,
        // the trade-off of it, is that we have to wait until delete note success to refresh tile.
        if (event && event.type === DELETE_NOTE) {
            this._store.select<any>(lastCustomActionSelector)
                .pipe(
                    filter(_event => _event.type === DELETE_NOTE_SUCCESS),
                    take(1)
                )
                .subscribe(() => this._reload(this._tileConfig, true));
        } else {
            this._reload(this._tileConfig, true);
        }
    }

    private _setLoadingStatus(status: boolean) {
        this.actionSubject$.next({
            id: this.id,
            type: TamDashboardActions.SET_LOADING_STATUS,
            payload: status
        });
    }
    private _storeFilter(event) {
        return (
            this._isUpdateAction(event) && this._noteIncluded(event.payload)
        ) || (
                this._isDeleteNoteAction(event) && event.payload && this._noteIncluded(event.payload.id)
            ) || (
                this._isDeleteAttachmentAction(event) && this._attachmentIncluded(event.payload));
    }

    private _isUpdateAction(action) {
        return action && action.type === UPDATE_NOTE_SUCCESS;
    }

    private _isDeleteNoteAction(action) {
        return action && (action.type === DELETE_NOTE || action.type === DELETE_SIDENOTE_SUCCESS);
    }

    private _isDeleteAttachmentAction(action) {
        return action && action.type === DELETE_ATTACHMENT_SUCCESS;
    }

    private _noteIncluded(noteId: string): boolean {
        return this._noteIds.includes(noteId);
    }

    private _attachmentIncluded(attachmentId: string): boolean {
        return this._attachmentIds.includes(attachmentId);
    }

    private _reload(config: any, hardRefresh = false) {
        this._dashboardTileService.noteTileLoadData(config, this._widget, hardRefresh).subscribe((data: any) => {
            if (data) {
                this.threadDetailConfigs = data.threadConfigs;
                this._takeDownIds();
            }
            if (config.saveStore) {
                this._store.dispatch({
                    type: DashboardActionTypes.SAVE_TILE_DATA,
                    payload: {
                        id: this.id,
                        data: data
                    }
                });
            }
            this.setTileData(data);
        });
    }

    private _takeDownIds() {
        this._noteIds = [];
        this._attachmentIds = [];
        Array.from(this.threadDetailConfigs.values()).forEach((config: ThreadDetailConfig) => {
            if (config.thread && config.thread.notes) {
                this._noteIds = this._noteIds.concat(config.thread.notes.map(note => note.id));
            }
            if (config.thread && config.thread.attachments) {
                this._attachmentIds = this._attachmentIds.concat(config.thread.attachments.keys());
            }
        });
    }
}
